import { Check, Target, Zap, MessageSquare, BarChart3, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";
import PaidAdsCalculator from "@/components/PaidAdsCalculator";
import PaidAdsComparison from "@/components/PaidAdsComparison";

const PaidAds = () => {
  return (
    <>
      <EnhancedSEO 
        type="service"
        title="Paid Ads Management | Campaign & Media Buying Services | VGP"
        description="Expert paid advertising management with full campaign execution, media buying, and automated lead capture with SMS integration. Get qualified leads on autopilot."
        keywords="paid ads management, media buying, Google Ads, Facebook Ads, lead automation, SMS marketing, campaign management, PPC services"
        url="/paid-ads"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32" style={{ background: 'var(--gradient-hero)' }}>
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Paid Ads That Actually Convert
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90 leading-relaxed">
                We handle everything: campaign strategy, media buying, and automated lead capture with SMS integration. You focus on closing deals.
              </p>
              <Button size="lg" variant="default" className="bg-white text-primary hover:bg-white/90" asChild>
                <Link to="/do-you-qualify">Get Your Free Ad Strategy</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* What We Do Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                  Full-Service Paid Advertising
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  From campaign creation to lead delivery, we handle every aspect of your paid advertising so you can focus on what you do best.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <Card className="p-8 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-primary/10 rounded-lg flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Campaign Management</h3>
                  <p className="text-muted-foreground mb-4">
                    We create, launch, and optimize your campaigns across Google Ads, Facebook, Instagram, and LinkedIn.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>Audience research & targeting</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>Ad creative development</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span>Daily optimization & testing</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-secondary/10 rounded-lg flex items-center justify-center mb-6">
                    <BarChart3 className="w-7 h-7 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Media Buying</h3>
                  <p className="text-muted-foreground mb-4">
                    Strategic ad spend management to maximize ROI and minimize wasted budget.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span>Budget allocation strategy</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span>Platform negotiation</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span>Performance tracking</span>
                    </li>
                  </ul>
                </Card>

                <Card className="p-8 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-accent/10 rounded-lg flex items-center justify-center mb-6">
                    <Zap className="w-7 h-7 text-accent" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-foreground">Lead Automation</h3>
                  <p className="text-muted-foreground mb-4">
                    Every lead is automatically captured, qualified, and delivered to your CRM with SMS follow-up.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Instant lead capture</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>CRM integration</span>
                    </li>
                    <li className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span>Automated SMS sequences</span>
                    </li>
                  </ul>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* ROI Calculator Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                Calculate Your Potential ROI
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                See what results you could achieve with our paid advertising services. Adjust the inputs to match your business.
              </p>
            </div>
            <PaidAdsCalculator />
          </div>
        </section>

        {/* SMS Automation Highlight */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                    <MessageSquare className="w-4 h-4" />
                    SMS Automation
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                    Never Miss a Lead Again
                  </h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    Our automated SMS system engages leads within seconds of submission. Pre-qualified prospects receive instant responses, appointment confirmations, and follow-ups - all on autopilot.
                  </p>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Instant Response</p>
                        <p className="text-sm text-muted-foreground">Engage leads within 60 seconds of form submission</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Personalized Sequences</p>
                        <p className="text-sm text-muted-foreground">Custom SMS flows based on lead behavior and preferences</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">Appointment Booking</p>
                        <p className="text-sm text-muted-foreground">Automated scheduling links sent via SMS for instant bookings</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                  <div className="space-y-4">
                    <div className="flex justify-end">
                      <div className="bg-primary text-white px-4 py-3 rounded-lg max-w-xs">
                        <p className="text-sm">Hi! I'm interested in your services</p>
                        <p className="text-xs opacity-70 mt-1">2:34 PM</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-muted px-4 py-3 rounded-lg max-w-xs">
                        <p className="text-sm text-foreground">Great! We'd love to help. What's the best time for a quick call?</p>
                        <p className="text-xs text-muted-foreground mt-1">2:34 PM • Auto-reply</p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-primary text-white px-4 py-3 rounded-lg max-w-xs">
                        <p className="text-sm">Tomorrow afternoon works</p>
                        <p className="text-xs opacity-70 mt-1">2:36 PM</p>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-muted px-4 py-3 rounded-lg max-w-xs">
                        <p className="text-sm text-foreground">Perfect! Book your slot here: [link]</p>
                        <p className="text-xs text-muted-foreground mt-1">2:36 PM • Auto-reply</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                How We Work
              </h2>
              <p className="text-xl text-muted-foreground">
                A proven process that delivers qualified leads consistently
              </p>
            </div>

            <div className="max-w-5xl mx-auto grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Strategy Session</h3>
                <p className="text-muted-foreground">We analyze your business, target audience, and competition</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Campaign Launch</h3>
                <p className="text-muted-foreground">We create and launch optimized campaigns across all platforms</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Lead Capture</h3>
                <p className="text-muted-foreground">Automated systems capture and qualify every lead instantly</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  4
                </div>
                <h3 className="text-xl font-bold mb-2 text-foreground">Continuous Optimization</h3>
                <p className="text-muted-foreground">Daily monitoring and optimization for maximum ROI</p>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
                Why Choose VGP Over Others?
              </h2>
              <p className="text-xl text-muted-foreground">
                See how our performance stacks up against industry averages
              </p>
            </div>
            <PaidAdsComparison />
          </div>
        </section>

        {/* Results Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Real Results From Real Campaigns
                </h2>
                <p className="text-xl text-white/90">
                  Numbers that matter to your bottom line
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold mb-2">2.4X</div>
                  <p className="text-lg text-white/90">Average ROAS</p>
                  <p className="text-sm text-white/70 mt-2">Return on Ad Spend across all clients</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold mb-2">68%</div>
                  <p className="text-lg text-white/90">Lower Cost Per Lead</p>
                  <p className="text-sm text-white/70 mt-2">Compared to industry averages</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold mb-2">85%</div>
                  <p className="text-lg text-white/90">Lead Response Rate</p>
                  <p className="text-sm text-white/70 mt-2">Thanks to SMS automation</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                No Long-Term Contracts
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-foreground">
                Ready to Scale Your Lead Generation?
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Book a free strategy call and we'll show you exactly how we can drive qualified leads to your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="premium" asChild>
                  <Link to="/do-you-qualify">Schedule Strategic Review</Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">View Pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PaidAds;
